
import Vue from 'vue';
import { Component } from 'nuxt-property-decorator';
import { MetaInfo } from 'vue-meta';
import HotelViewRoot from '../../-index';
import { MyHotelLocalNavi } from '../-components';
import { HSimpleEmbedMap } from '~/components/HSimpleMap';
import { HotelDetail, AccessInfo, NavigationItem } from '~/schemes';
import {
  HSimpleLinks,
  HBtn,
  HPrecautionList,
  HSimpleCoverTitle,
  HThemeDecoratedTitle,
  HImg,
} from '~/components';
import { TOUR_LIST_LINK_OPTIONS } from '~/helpers';

export interface MyHotelAccessProps {
  hotel: HotelDetail;
}

@Component<MyView>({
  inject: ['hotelViewRoot'],
  provide() {
    return {
      tourList: TOUR_LIST_LINK_OPTIONS.accessLink,
    };
  },
  async asyncData({ $hotel, params, error, $error }) {
    try {
      const { hotel_slug } = params;
      let hotel: HotelDetail | null | undefined = $hotel.current;
      if (!hotel) hotel = await $hotel.getBySlug(hotel_slug);
      if (!hotel || !hotel.accessSettings) {
        return error({
          statusCode: 404,
        });
      }
      const { hasAccessContents } = hotel;
      if (!hasAccessContents) {
        return error({
          statusCode: 404,
        });
      }

      const { informations } = await $hotel.accessInformationsBySlug(
        hotel_slug,
      );

      return {
        informations,
      };
    } catch (_err) {
      $error.throw(_err);
    }
  },
  head() {
    const { hotel, title } = this;
    const head: MetaInfo = {
      title,
      meta: [
        { hid: 'og:title', property: 'og:title', content: title },
        {
          hid: 'og:url',
          property: 'og:url',
          content: `${this.$navigation.origin}/${this.$language.current}/hotels/${hotel.slug}/access/`,
        },
      ],
    };
    return head;
  },
  render() {
    const { hotel, transportInformation, mapLinks } = this;
    const multipleAccessContents = this.informations.length >= 2;
    return (
      <div staticClass="my-hotel-access-index-view">
        <MyHotelLocalNavi />
        <div staticClass="my-head">
          <HSimpleCoverTitle
            staticClass="my-head__title"
            title={this.title}
            deco={this.titleDeco}
          />

          <div staticClass="my-inner">
            <div staticClass="my-head__map">
              <p
                staticClass="my-head__map__text"
                domPropsInnerHTML={
                  this.$t('value.address', hotel) as string
                }></p>

              <HSimpleEmbedMap hotel={hotel} />

              {mapLinks.length > 0 && (
                <div staticClass="my-head__map__links">
                  <HSimpleLinks items={mapLinks} />
                </div>
              )}
            </div>
          </div>
        </div>

        {!!multipleAccessContents && (
          <div staticClass="my-internal-links">
            <HThemeDecoratedTitle
              staticClass="my-internal-links__title"
              size="sm">
              {transportInformation}
            </HThemeDecoratedTitle>
            <HSimpleLinks
              page
              narrowBlocks
              items={this.informations.map((item, index) => ({
                key: item.id,
                label: item.title,
                href: `#access-${item.id}`,
              }))}
            />
          </div>
        )}
        <div staticClass="my-main-wrapper">
          {this.informations.map((item, index) => (
            <div staticClass="my-main" key={index} id={`access-${item.id}`}>
              <div staticClass="my-inner">
                <HThemeDecoratedTitle staticClass="my-main__title" size="md">
                  {item.title}
                </HThemeDecoratedTitle>
                {item.contents.map((content, index) => (
                  <div staticClass="my-main__list" key={index}>
                    {!!content.title && (
                      <HThemeDecoratedTitle
                        staticClass="my-main__list__title"
                        size="sm">
                        {content.title}
                      </HThemeDecoratedTitle>
                    )}
                    {/* 埋め込みマップ */}
                    {content.isEmbedMap && !!content.embedMap && (
                      <HSimpleEmbedMap embedMap={content.embedMap} />
                    )}
                    {/* 画像 */}
                    {!content.isEmbedMap && !!content.image && (
                      <HImg src={content.image} />
                    )}
                    <div
                      staticClass="my-main__list__text"
                      v-wysiwyg={content.description}></div>
                    {content.links.length > 0 && (
                      <div staticClass="my-main__list__links">
                        {content.links.map((link, index) => {
                          const location = link.url
                            ? this.$navigation.resolveHrefTo(link.url)
                            : undefined;
                          return (
                            <div
                              staticClass="my-main__list__links__link-wrapper"
                              key={index}>
                              <HBtn
                                staticClass="my-main__list__links__link"
                                props={location}
                                color="skelton">
                                {link.text}
                              </HBtn>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {content.precautions.length > 0 && (
                      <div staticClass="my-main__list__precautions-wrapper">
                        <HPrecautionList
                          items={content.precautions.map(
                            (precaution, index) => precaution,
                          )}
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  },
})
export default class MyView extends Vue {
  readonly hotelViewRoot!: HotelViewRoot;
  readonly informations: AccessInfo[] = [];

  /**
   * タイトルのデコレーション
   *
   * * リゾナーレの時だけ、欧文テキストを記載する
   *
   * @see https://hoshinoresorts-dev.slack.com/archives/C01PKFF4753/p1668563143395969
   */
  get titleDeco() {
    if (!this.$theme.is('risonare') || this.$language.current === 'en') {
      return;
    }
    return 'Access';
  }

  get title() {
    return this.$t('name.access') as string;
  }

  get hotel() {
    return this.hotelViewRoot.hotel;
  }

  get localeMap() {
    return this.$t('label.localeMap') as string;
  }

  get transportInformation() {
    return this.$t('label.transportInformation') as string;
  }

  get accessSettings() {
    const { accessSettings } = this.hotel;
    if (!accessSettings) {
      throw new Error('missing accessSettings');
    }
    return accessSettings;
  }

  get accessSettingslinks() {
    const { accessSettings } = this;
    if (accessSettings && accessSettings.links) {
      return accessSettings.links;
    }
  }

  get mapLinks() {
    const links: NavigationItem[] = [];
    if (this.hotel.mapUrl) {
      links.push({
        key: 'goolemap',
        label: this.localeMap,
        href: this.hotel.mapUrl,
      });
    }
    if (this.accessSettingslinks) {
      this.accessSettingslinks.forEach((item, index) =>
        links.push({ key: `link-${index}`, label: item.text, href: item.url }),
      );
    }
    return links;
  }
}
